import { inject, Injectable } from '@angular/core';
import { Action, Actions, ofActionSuccessful, State, StateContext, Store } from '@ngxs/store';
import { ChatInitiatedAction } from './chat.actions';
import { ChatClient } from '@smatchy/chat-client';
import { AuthSelector, SetAccessTokenAction, SetUserProfileAction, UserProfileSelector } from '@smatchy/core';
import { environment } from '../../../../../environments/environment';

export interface ChatStateModel {
  /** Absent until the chat client connects, which is what the defaults set. */
  clientSdk?: ChatClient;
}

const defaults: ChatStateModel = {
  clientSdk: undefined,
};

@State<ChatStateModel>({
  name: 'chat',
  defaults,
})
@Injectable()
export class ChatState {
  #store = inject(Store);
  #actions = inject(Actions);
  #chatSDk: ChatClient;

  constructor() {
    this.#chatSDk = new ChatClient({ serverUrl: environment.api });
    this.#actions.pipe(ofActionSuccessful(SetAccessTokenAction, SetUserProfileAction)).subscribe(() => {
      // Read once into a local: the test and the two reads below were three
      // separate snapshots of a store that can change between them, and the
      // `if` narrowed nothing for the reads.
      const profile = this.#store.selectSnapshot(UserProfileSelector.profile);
      const token = this.#store.selectSnapshot(AuthSelector.accessToken);

      if (profile && token) {
        // User connected
        this.#chatSDk.connect({
          userId: profile.userId,
          token,
        });

        this.#store.dispatch(new ChatInitiatedAction(this.#chatSDk));
      }
    });
  }

  @Action(ChatInitiatedAction)
  add({ patchState }: StateContext<ChatStateModel>, { payload }: ChatInitiatedAction) {
    patchState({
      clientSdk: payload,
    });
  }
}
